import React, { useMemo, useState } from 'react';
import { Search, Check, Loader2 } from 'lucide-react';
import '../wizard/steps/ServicesStep.css';

export interface LookupOption {
  id: number;
  name: string;
  /** Optional group label; when provided and groupBy is true, options are rendered in groups. */
  group?: string | null;
  /** Optional secondary line (e.g. a procedure code). */
  code?: string | null;
}

interface LookupMultiSelectProps {
  options: LookupOption[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  /** Singular noun used in counts / empty states, e.g. "capability", "category", "service". */
  itemNoun?: string;
  /** Plural override; defaults to `${itemNoun}s`. */
  itemNounPlural?: string;
  groupBy?: boolean;
  isLoading?: boolean;
  loadError?: string | null;
  onRetry?: () => void;
  searchPlaceholder?: string;
}

/**
 * Reusable searchable multi-select list (search box + Select/Clear all + grouped
 * checkbox list). Generalizes the original ServicesForm pattern so categories,
 * capabilities, and procedures all share one component and one stylesheet.
 */
const LookupMultiSelect: React.FC<Readonly<LookupMultiSelectProps>> = ({
  options,
  selectedIds,
  onChange,
  itemNoun = 'item',
  itemNounPlural,
  groupBy = false,
  isLoading = false,
  loadError = null,
  onRetry,
  searchPlaceholder,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const plural = itemNounPlural ?? `${itemNoun}s`;

  const filtered = useMemo(() => {
    if (!searchTerm.trim()) return options;
    const term = searchTerm.toLowerCase();
    return options.filter(
      (o) =>
        o.name.toLowerCase().includes(term) ||
        (o.code?.toLowerCase().includes(term) ?? false) ||
        (o.group?.toLowerCase().includes(term) ?? false)
    );
  }, [options, searchTerm]);

  const grouped = useMemo(() => {
    if (!groupBy) return null;
    const groups: Record<string, LookupOption[]> = {};
    filtered.forEach((o) => {
      const g = o.group || 'Uncategorized';
      (groups[g] ??= []).push(o);
    });
    return Object.entries(groups).sort(([a], [b]) => {
      if (a === 'Uncategorized') return 1;
      if (b === 'Uncategorized') return -1;
      return a.localeCompare(b);
    });
  }, [filtered, groupBy]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const toggle = (id: number) => {
    if (selectedSet.has(id)) {
      onChange(selectedIds.filter((x) => x !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const handleSelectAll = () => {
    const merged = new Set(selectedIds);
    filtered.forEach((o) => merged.add(o.id));
    onChange(Array.from(merged));
  };

  const handleClearAll = () => {
    if (!searchTerm.trim()) {
      onChange([]);
      return;
    }
    // When filtered, only clear the currently visible items.
    const visible = new Set(filtered.map((o) => o.id));
    onChange(selectedIds.filter((id) => !visible.has(id)));
  };

  if (isLoading) {
    return (
      <div className="services-section">
        <div className="services-loading">
          <Loader2 className="spinner" size={24} />
          <span>Loading {plural}...</span>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="services-section">
        <div className="services-error">
          <p>{loadError}</p>
          {onRetry && (
            <button type="button" className="btn-retry" onClick={onRetry}>
              Try Again
            </button>
          )}
        </div>
      </div>
    );
  }

  const renderItem = (o: LookupOption) => {
    const isSelected = selectedSet.has(o.id);
    return (
      <div
        key={o.id}
        role="checkbox"
        aria-checked={isSelected}
        tabIndex={0}
        className={`service-item ${isSelected ? 'selected' : ''}`}
        onClick={() => toggle(o.id)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle(o.id);
          }
        }}
      >
        <div className={`service-checkbox ${isSelected ? 'checked' : ''}`}>
          {isSelected && <Check size={14} />}
        </div>
        <div className="service-info">
          <span className="service-name">{o.name}</span>
          {o.code && <span className="service-code">{o.code}</span>}
        </div>
      </div>
    );
  };

  return (
    <div className="services-section">
      <div className="services-toolbar">
        <div className="search-box">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            placeholder={searchPlaceholder ?? `Search ${plural}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label={`Search ${plural}`}
          />
        </div>
        <div className="selection-actions">
          <button type="button" className="btn-link" onClick={handleSelectAll}>
            Select All
          </button>
          <span className="divider">|</span>
          <button type="button" className="btn-link" onClick={handleClearAll}>
            Clear All
          </button>
        </div>
      </div>

      <div className="selection-summary">
        <span className="selection-count">{selectedIds.length}</span> {selectedIds.length !== 1 ? plural : itemNoun} selected
      </div>

      <div className="services-list">
        {filtered.length === 0 ? (
          <div className="no-results">
            {searchTerm ? <p>No {plural} found matching "{searchTerm}"</p> : <p>No {plural} available</p>}
          </div>
        ) : grouped ? (
          grouped.map(([groupName, items]) => (
            <div key={groupName} className="service-group">
              <div className="group-header">
                <span className="group-name">{groupName}</span>
                <span className="group-count">{items.length}</span>
              </div>
              <div className="group-services">{items.map(renderItem)}</div>
            </div>
          ))
        ) : (
          <div className="group-services">{filtered.map(renderItem)}</div>
        )}
      </div>
    </div>
  );
};

export default LookupMultiSelect;
